"use strict"

import React, { useCallback, useState, useEffect } from "react"

import {
  cleanObservationAxisAssignment,
  getMatchingTrend,
  isSupplementalCalcPlot,
  getUniqueTags,
  isHomeDashPlot,
} from "../../../resources/utils"
import AssignTagAxisDropdown from "../../widgets/AssignTagAxisDropdown"
import TrendEditorPopover from "../../widgets/TrendEditorPopover"
import UnitsSelectorAndTagInfo from "../../widgets/UnitsSelectorAndTagInfo"
import TrendRemover from "../../widgets/TrendRemover"
import ObservationKeyMarker from "../../GraphKeys/ObservationKeyMarker"
import { deepcopy } from "../../../../utils"
import LabelForSeries from "../LabelForSeries"

export function ObservationKey({
  tag_category = "observations",
  observations = [], // Either graph 'observations' or 'tags' as passed by parent
  observationTypes = {},
  graphKey = {},
  download = false,
  liveView = false,
  spacing = 20,
  plotConfigUpdater,
  graphIndex,
  intl,
}) {
  const toggleSeriesDisable = useCallback(
    (key) => {
      let graph = Object.assign({}, deepcopy(graphKey)),
        toggled = false

      const nonDisabledTrendCount = graph[tag_category].filter((series) => !series.disabled).length

      const observations = graph[tag_category].map((o) => {
        const match = getMatchingTrend(o, key)
        if (!match) {
          return o
        } else {
          if (!o.disabled && nonDisabledTrendCount > 1) {
            toggled = true
            return {
              ...o,
              disabled: true,
            }
          } else if (o.disabled) {
            toggled = true
            return {
              ...o,
              disabled: false,
            }
          } else {
            return o
          }
        }
      })

      if (toggled) {
        if (!graph.special || graph.special === "multi") {
          graph = cleanObservationAxisAssignment(observations, graph, tag_category)
        } else {
          // TODO - explore the else conditions - site-wide perhaps? Is this needed?
          graph[tag_category] = observations
        }

        plotConfigUpdater(graph)
      }
    },
    [graphKey, plotConfigUpdater, tag_category],
  )

  const graph = Object.assign({}, graphKey)
  const [unifiedTagPopper, setUnifiedTagPopper] = useState(false)
  const [individualTagPopper, setIndividualTagPopper] = useState(false)
  const [canToggleTags, setCanToggleTags] = useState(false)
  const [canRemoveTags, setCanRemoveTags] = useState(false)
  useEffect(() => {
    const suppCalcPlot = isSupplementalCalcPlot(graph)

    // Add any non-observation 'special' graph types here to exclude from 'tag' specific menu options
    const nonObservationGraph = ["rnaseq", "microbial-community"].includes(graph.special)

    const excludedPlot = download || suppCalcPlot || nonObservationGraph

    // Show only 1 unified TagInfoPopper and units toggle dropdown
    const singleTagPopper = graph.special === "site-wide"
    setUnifiedTagPopper(!excludedPlot && singleTagPopper)

    // Show a TagInfoPopper and units toggle dropdown for each trend/key row
    setIndividualTagPopper(!excludedPlot && !singleTagPopper)

    // User allowed to: Alter Y1/Y2 axes assignment
    setCanToggleTags(!excludedPlot || (!download && graph.special === "microbial-community"))

    // User allowed to: Remove trends from graph
    setCanRemoveTags(
      !download &&
        !suppCalcPlot &&
        !isHomeDashPlot(graphKey) &&
        (!graphKey?.special || graphKey.special === "multi"),
    )
  }, [graph, download])

  let unifiedTagData = {}
  if (unifiedTagPopper) {
    const first_obs = graph.observations[0]
    unifiedTagData = { ...(observationTypes[first_obs?.tag] || {}), units: first_obs.units }
  }

  const methodProps = {
    graphKey: graphKey,
    tag_category: tag_category,
    plotConfigUpdater: plotConfigUpdater,
  }

  const content = (
    <div style={download || liveView ? { display: "table", margin: "0 auto", marginTop: "5px" } : {}}>
      {observations
        .filter((obs) => (download && !obs.disabled) || !download)
        .map((obs, idx) => {
          return (
            <div
              key={`${idx}_${obs.tag}`}
              style={{
                alignItems: "center",
                ...(download || liveView
                  ? {
                      float: "left",
                      marginRight: "3px",
                      marginBottom: "1px",
                      padding: "2px 4px",
                    }
                  : {}),
                ...(liveView
                  ? {
                      borderRadius: "3px",
                      backgroundColor: "var(--lanzalake-blue-bright)",
                    }
                  : {}),
              }}
              className={liveView ? "solid-grey-border flex-r" : "flex-r"}
            >
              <div style={{ display: "flex", alignItems: "center" }}>
                {!obs.disableDelete && canRemoveTags && getUniqueTags(graph, tag_category).length > 1 && (
                  <>
                    <TrendRemover
                      obv_info={obs}
                      observationTypes={observationTypes}
                      intl={intl}
                      {...methodProps}
                    />{" "}
                    &nbsp;
                  </>
                )}
                <svg width={20} height={spacing}>
                  {!download ? (
                    <TrendEditorPopover
                      observations={observations}
                      obs={obs}
                      spacing={spacing}
                      graphIndex={graphIndex}
                      intl={intl}
                      {...methodProps}
                    />
                  ) : (
                    <g
                      transform={`translate(10, ${spacing / 2})`}
                      style={{
                        opacity: obs.disabled ? "0.45" : "1.0",
                      }}
                    >
                      <ObservationKeyMarker {...obs} />
                    </g>
                  )}
                </svg>
                <div>
                  <div
                    style={{
                      cursor: "" + (obs.style !== "box" && !download ? "pointer" : "default"),
                      color: "" + (obs.disabled ? "var(--lanzalake-grey)" : "var(--lanzalake-black)"),
                    }}
                    onClick={() => {
                      obs.style !== "box" && !download ? toggleSeriesDisable(obs) : null
                    }}
                  >
                    <LabelForSeries obs={obs} observationTypes={observationTypes} download={download} />
                  </div>
                </div>
              </div>
              &nbsp;
              <div style={{ display: "flex", marginLeft: "auto" }}>
                {individualTagPopper && (
                  <UnitsSelectorAndTagInfo
                    obs={obs}
                    tagData={{ disabled: obs.disabled || false, ...(observationTypes[obs.tag] || {}) }}
                    {...methodProps}
                  />
                )}
                &nbsp;
                {canToggleTags && obs.style !== "box" && (
                  <AssignTagAxisDropdown
                    obv_info={obs}
                    liveView={liveView}
                    disabled={observations.length <= 1}
                    {...methodProps}
                  />
                )}
              </div>
            </div>
          )
        })}
    </div>
  )

  return (
    <>
      {unifiedTagPopper && (
        <div style={{ display: "flex" }}>
          <UnitsSelectorAndTagInfo
            obs={{
              tag: unifiedTagData.tag_name || unifiedTagData.tag || graph.title,
              units: unifiedTagData.units,
            }}
            tagData={unifiedTagData}
            {...methodProps}
          />
        </div>
      )}
      <div>{content}</div>
    </>
  )
}
